import React, { useEffect, useRef, useState } from 'react';
import cn from 'classnames';
import { range } from 'lodash';
// @ts-ignore
import Glide from '@glidejs/glide/dist/glide.esm';
import { IBaseElementProps } from 'tbk-components/src/components/BasicElement';
import CarouselItem from '../CarouselItem';
import { BgColor } from 'tbk-components/src/components/Core/colors';
import { IImage } from 'tbk-components/src/components/Image';

export interface ICarouselProps extends IBaseElementProps {
	showArrows?: boolean;
	showBullets?: boolean;
	perView?: number;
	startAt?: number;
	children?: any;
	backgroundColor?: BgColor;
	backgroundImage?: IImage;
	textColor?: 'light' | 'dark';
	options?: { [x: string]: any };
	onMount?: (carousel: any, ref: HTMLDivElement) => void;
}

/**
 * Carousel
 * @block
 * @requiresHooks
 */
const Carousel: React.FC<ICarouselProps> = ({
	children,
	className,
	classNames = [],
	showArrows = true,
	showBullets = true,
	perView = 1,
	startAt = 0,
	backgroundColor = 'transparent',
	backgroundImage,
	textColor,
	options = {},
	onMount
}) => {
	const [numSlides, setNumSlides] = useState(0);
	const ref = useRef(null);
	const id = `carousel-${`${Math.random()}`.substr(2)}`;

	useEffect(() => {
		setTimeout(() => {
			if (!ref.current) {
				return;
			}

			const carousel = ref.current! as HTMLDivElement;

			const totalSlides = carousel.querySelectorAll('.glide__slide')
				.length;
			setNumSlides(totalSlides);

			if (carousel.dataset.initialized === 'true' || numSlides < 1) {
				// either already initialized or not ready with numSlides yet.
				return;
			}

			const glide = new Glide(`#${id}`, {
				type: 'carousel',
				startAt,
				perView,
				...options,
			});

			glide.on('move', () => {
				carousel.classList.add('moving');
			});

			glide.on('move.after', () => {
				carousel.classList.remove('moving');
			});

			const mountCarousel = () => {
				if (
					carousel.dataset.initialized === 'true' ||
					totalSlides < 1
				) {
					return;
				}
				carousel.dataset.initialized = 'true';
				glide.mount();
				onMount && onMount(glide, ref.current!);
			};

			// Initialize inside tabs
			const tabPane = carousel.closest('.tab-pane');
			if (tabPane !== null && !tabPane.classList.contains('active')) {
				const button = document.querySelector(`#${tabPane.id}-tab`);
				button &&
					button!.addEventListener('shown.bs.tab', mountCarousel);
			} else {
				mountCarousel();
			}
		});
	}, [startAt, perView, id, numSlides]);

	return (
		<div
			className={
				className ||
				cn(
					'glide',
					'carousel',
					`carousel--items-${numSlides}`,
					`bg-${backgroundColor}`,
					!textColor && backgroundColor === 'dark' && `text-light`,
					textColor && `text-${textColor}`,
					numSlides > 0 && 'pb-4',
					backgroundImage && 'has-background',
					...classNames,
				)
			}
			style={
				backgroundImage && {
					backgroundImage: `url('${backgroundImage.src}')`,
				}
			}
			ref={ref}
			id={id}
		>
			<div className="glide__track" data-glide-el="track">
				<ul className="glide__slides">{children}</ul>
			</div>

			{showArrows && children && (
				<div className="glide__arrows" data-glide-el="controls">
					<button
						className="btn btn-default glide__arrow glide__arrow--left"
						data-glide-dir="<"
						aria-label="Previous"
					>
						<i className="icon icon-chevron-left" />
					</button>
					<button
						className="btn btn-default glide__arrow glide__arrow--right"
						data-glide-dir=">"
						aria-label="Next"
					>
						<i className="icon icon-chevron-right" />
					</button>
				</div>
			)}

			{showBullets && (
				<div className="glide__bullets" data-glide-el="controls[nav]">
					{numSlides > 0 &&
						range(0, numSlides).map((_, index) => (
							<button
								key={index}
								className="glide__bullet"
								data-glide-dir={`=${index}`}
								aria-label={`Go to slide ${index + 1}`}
							></button>
						))}
				</div>
			)}
		</div>
	);
};

export { CarouselItem };

export default Carousel;
